import { MediaDevices } from "./media-devices-utils";

export const DIALOG_MEDIA_QUALITY_CHANGED = "dialog-media-quality-changed";
export const DIALOG_MEDIA_QUALITY_CHANGED_NOTIFICATION = "roomMediaQualityChanged";
export const SCREEN_SHARE_CAMERA_MAX_BITRATES = Object.freeze([250000, 600000, 1200000]);

export const DIALOG_MEDIA_QUALITY_PROFILES = Object.freeze({
  low: Object.freeze({
    version: 1,
    profile: "low",
    videoGoogleStartBitrate: 1000,
    camera: Object.freeze({
      width: 1280,
      height: 720,
      frameRate: 30,
      encodings: Object.freeze([
        Object.freeze({ scaleResolutionDownBy: 4, maxBitrate: 250000 }),
        Object.freeze({ scaleResolutionDownBy: 2, maxBitrate: 800000 }),
        Object.freeze({ scaleResolutionDownBy: 1, maxBitrate: 1500000 })
      ])
    }),
    screen: Object.freeze({
      frameRate: 30,
      encodings: Object.freeze([
        Object.freeze({ dtx: true, maxBitrate: 750000 }),
        Object.freeze({ dtx: true, maxBitrate: 1500000 })
      ])
    })
  }),
  standard: Object.freeze({
    version: 1,
    profile: "standard",
    videoGoogleStartBitrate: 1500,
    camera: Object.freeze({
      width: 1280,
      height: 720,
      frameRate: 30,
      encodings: Object.freeze([
        Object.freeze({ scaleResolutionDownBy: 4, maxBitrate: 350000 }),
        Object.freeze({ scaleResolutionDownBy: 2, maxBitrate: 1000000 }),
        Object.freeze({ scaleResolutionDownBy: 1, maxBitrate: 2000000 })
      ])
    }),
    screen: Object.freeze({
      frameRate: 30,
      encodings: Object.freeze([
        Object.freeze({ dtx: true, maxBitrate: 1500000 }),
        Object.freeze({ dtx: true, maxBitrate: 4000000 })
      ])
    })
  }),
  high: Object.freeze({
    version: 1,
    profile: "high",
    videoGoogleStartBitrate: 2500,
    camera: Object.freeze({
      width: 1920,
      height: 1080,
      frameRate: 30,
      encodings: Object.freeze([
        Object.freeze({ scaleResolutionDownBy: 4, maxBitrate: 800000 }),
        Object.freeze({ scaleResolutionDownBy: 2, maxBitrate: 1800000 }),
        Object.freeze({ scaleResolutionDownBy: 1, maxBitrate: 4000000 })
      ])
    }),
    screen: Object.freeze({
      frameRate: 30,
      encodings: Object.freeze([
        Object.freeze({ dtx: true, maxBitrate: 2500000 }),
        Object.freeze({ dtx: true, maxBitrate: 6000000 })
      ])
    })
  })
});

export const DIALOG_MEDIA_QUALITY_PROFILE_ORDER = Object.freeze(["low", "standard", "high"]);

function profileName(input) {
  if (typeof input === "string") {
    return input;
  }

  return input?.profile;
}

function isKnownProfile(profile) {
  return Object.prototype.hasOwnProperty.call(DIALOG_MEDIA_QUALITY_PROFILES, profile);
}

function cloneEncodings(encodings) {
  return encodings.map(encoding => ({ ...encoding }));
}

function capCameraEncodingsForScreenShare(encodings) {
  const fallbackBitrate = SCREEN_SHARE_CAMERA_MAX_BITRATES[SCREEN_SHARE_CAMERA_MAX_BITRATES.length - 1];

  return encodings.map((encoding, index) => ({
    ...encoding,
    maxBitrate: Math.min(encoding.maxBitrate || Infinity, SCREEN_SHARE_CAMERA_MAX_BITRATES[index] || fallbackBitrate)
  }));
}

export function normalizeDialogMediaQualityProfile(input) {
  const name = profileName(input);

  if (name === "high") {
    return DIALOG_MEDIA_QUALITY_PROFILES.high;
  }
  if (name === "standard") {
    return DIALOG_MEDIA_QUALITY_PROFILES.standard;
  }

  return DIALOG_MEDIA_QUALITY_PROFILES.low;
}

function normalizeAllowedProfiles(input, fallback = DIALOG_MEDIA_QUALITY_PROFILE_ORDER) {
  const source = Array.isArray(input) && input.length > 0 ? input : fallback;
  const profiles = [];

  for (const profile of source || []) {
    if (isKnownProfile(profile) && !profiles.includes(profile)) {
      profiles.push(profile);
    }
  }

  return profiles.length > 0 ? profiles : [...DIALOG_MEDIA_QUALITY_PROFILE_ORDER];
}

function highestAllowedProfile(allowedProfiles) {
  return (
    [...allowedProfiles].sort(
      (a, b) => DIALOG_MEDIA_QUALITY_PROFILE_ORDER.indexOf(a) - DIALOG_MEDIA_QUALITY_PROFILE_ORDER.indexOf(b)
    )[allowedProfiles.length - 1] || "low"
  );
}

export function normalizeDialogMediaQualityState(input, currentState = null) {
  const profile = normalizeDialogMediaQualityProfile(profileName(input) ? input : currentState).profile;
  const allowedProfiles = normalizeAllowedProfiles(input?.allowedProfiles, currentState?.allowedProfiles);
  const maxProfile = isKnownProfile(input?.maxProfile)
    ? input.maxProfile
    : currentState?.maxProfile || highestAllowedProfile(allowedProfiles);
  const canChange =
    typeof input?.canChange === "boolean"
      ? input.canChange
      : currentState?.canChange === true && allowedProfiles.length > 1;
  return {
    version: 1,
    profile,
    allowedProfiles,
    maxProfile,
    canChange
  };
}

export function cameraProducerOptionsForMediaQuality(input, { screenShareActive = false } = {}) {
  const profile = normalizeDialogMediaQualityProfile(input);
  const encodings = cloneEncodings(profile.camera.encodings);

  return {
    codecOptions: { videoGoogleStartBitrate: profile.videoGoogleStartBitrate },
    encodings: screenShareActive ? capCameraEncodingsForScreenShare(encodings) : encodings
  };
}

export function screenProducerOptionsForMediaQuality(input) {
  const profile = normalizeDialogMediaQualityProfile(input);

  return {
    codecOptions: { videoGoogleStartBitrate: profile.videoGoogleStartBitrate },
    encodings: cloneEncodings(profile.screen.encodings)
  };
}

export function rtpEncodingParametersForMediaQuality(input, source, { screenShareActive = false } = {}) {
  const { encodings } =
    source === MediaDevices.SCREEN
      ? screenProducerOptionsForMediaQuality(input)
      : cameraProducerOptionsForMediaQuality(input, { screenShareActive });
  const maxBitrate = Math.max(...encodings.map(encoding => encoding.maxBitrate || 0));

  return { maxBitrate };
}
